import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api.js';

/**
 * useVerify — fetch the PUBLIC verify endpoint.
 *
 * Endpoint: GET /api/verify/:tenantSlug/:staffSlug
 *
 * This is the endpoint members of the public hit when they scan a QR code.
 * It's UNAUTHENTICATED. Returns one of 4 states:
 *
 *   verified              — staff is active
 *   under_review          — staff is suspended
 *   no_longer_employed    — staff has left
 *   unknown               — staff slug didn't match, or tenant slug didn't match
 *
 * The endpoint also logs a scan as a side effect (with 1-hour dedupe).
 * That means the preview page does log scans when tested — worth being
 * aware of but not a problem since dev tenants' logs are expected to
 * have some test data in them.
 */
export function useVerify(tenantSlug, staffSlug) {
  const [state, setState] = useState({
    data: null,
    loading: true,
    error: null,
  });

  const load = useCallback(async () => {
    if (!tenantSlug || !staffSlug) {
      setState({ data: null, loading: false, error: new Error('Missing slug') });
      return;
    }
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const data = await api.get(`/api/verify/${encodeURIComponent(tenantSlug)}/${encodeURIComponent(staffSlug)}`);
      setState({ data, loading: false, error: null });
    } catch (e) {
      setState({ data: null, loading: false, error: e });
    }
  }, [tenantSlug, staffSlug]);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, refresh: load };
}
